import { Warning } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  Chip,
  CSSObject,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { enGB } from 'date-fns/locale/en-GB';
import { it } from 'date-fns/locale/it';
import { sl } from 'date-fns/locale/sl';
import I18n from 'i18next';
import {
  Controller,
  FieldValues,
  Path,
  UseControllerProps,
  UseFormRegister
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ICustomOption } from "../interfaces/form";

interface ICustomInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>
> extends UseControllerProps<TFieldValues, TName> {
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  options?: ICustomOption[];
  register: UseFormRegister<any>;
  customStyles?: CSSObject;
  link?: string;
  linkLabel?: string;
}

// rendering MUI text field with react-hook-form wrapper
export const CustomTextField = (props: ICustomInputProps) => {
  const { t } = useTranslation();
  const { name, label, control, register } = props;
  const id = `${name}-${label}`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl error={!!error} sx={{ marginY: "0.5em", width: props.customStyles ? props.customStyles.width : "100%" }}>
          <TextField
            {...register(name)}
            {...field}
            type={name === "password" || name === "confirmPassword" || name === "newPassword" || name === "confirmNewPassword" ? "password" : "text"}
            id={id}
            helperText={error && error.message ? t(error.message) : null}
            size="small"
            error={!!error}
            fullWidth
            label={label ? t(label) : ""}
            variant="outlined"
          />
        </FormControl>
      )}
    />
  );
};

// rendering MUI select field with react-hook-form wrapper
export const CustomSelectField = (props: ICustomInputProps) => {
  const { t } = useTranslation();
  const { control, name, label, options, register, disabled } = props;
  const id = name + "-" + label;
  const labelId = name + "-" + label + "-label";

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <FormControl error={!!error} size="small" sx={{ marginY: "0.5em", width: "100%" }}>
          <InputLabel id={labelId}>{label ? t(label) : ""}</InputLabel>
          <Select
            {...register(name)}
            id={id}
            value={value}
            onChange={onChange}
            disabled={disabled}
            label={label ? t(label) : ""}
            labelId={labelId}
            MenuProps={{
              sx: { height: "248px" },
            }}
            sx={{ width: "100%" }}
          >
            <MenuItem value="">
              <em>{t("beehivesPage.genericSelectLabel")}</em>
            </MenuItem>
            {options &&
              options.map((option, index) => (
                <MenuItem
                  key={option.value + "-" + index}
                  value={option.value}
                >
                  {t(option.desc) || option.desc}
                </MenuItem>
              ))}
          </Select>
          {
            name === "areaId" && disabled &&
            <FormHelperText sx={{ marginX: "14px", marginTop: "4px" }}>
              {t("usersPage.usersForm.cannotChangeArea")}
            </FormHelperText>
          }
          <FormHelperText error sx={{ marginX: "14px", marginTop: "4px" }}>
            {error && error.message ? t(error.message) : undefined}
          </FormHelperText>
        </FormControl>
      )}
      defaultValue=""
    />
  );
};

// rendering MUI multiselect/chips field with react-hook-form wrapper
export const CustomMultiSelectChipsField = (props: ICustomInputProps) => {
  const { t } = useTranslation();
  const { control, name, label, options, register, disabled } = props;
  const id = name + "-" + label;
  const labelId = name + "-" + label + "-label";

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const allSelected = value.length === options?.length;
        return (
          <FormControl error={!!error} size="small" sx={{ marginY: "0.5em", width: "100%" }}>
            <InputLabel id={labelId}>{label ? t(label) : ""}</InputLabel>
            <Select
              {...register(name)}
              id={id}
              value={value}
              label={label ? t(label) : ""}
              labelId={labelId}
              multiple
              onChange={(ev) => {
                if (options && ev.target.value.includes("select-all")) {
                  onChange(allSelected ? [] : [...options.map(el => el.value)]);
                } else {
                  onChange(ev.target.value);
                }
              }}
              disabled={disabled}
              MenuProps={{
                sx: { height: "448px" },
              }}
              sx={{ width: "100%" }}
              input={<OutlinedInput id={labelId + "-multiple-chip"} label="Chip" />}
              renderValue={(selected: string[]) => {
                return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((option) => {
                    const currentOption = options?.find(opt => opt.value === option)
                    return currentOption &&
                      option !== '' &&
                      <Chip variant="filled" color="primary" key={currentOption.value} label={currentOption.desc} />
                  })}
                </Box>
              }}
            >
              <MenuItem value="">
                <em>{t("beehivesPage.genericSelectLabel")}</em>
              </MenuItem>
              <MenuItem value="select-all" divider>
                <Checkbox checked={allSelected} sx={{ p: 0.5, pr: 1 }}></Checkbox>
                <Typography sx={{ fontWeight: 500 }}>{t("beehivesPage.selectUnselectAllLabel")}</Typography>
              </MenuItem>
              {options &&
                options.map((option, index) => (
                  <MenuItem
                    key={option.value + "-" + index}
                    value={option.value}
                  >
                    <Checkbox checked={!!value.find((el: string) => el === option.value)} sx={{ p: 0.5, pr: 1 }}></Checkbox>
                    {t(option.desc) || option.desc}
                  </MenuItem>
                ))}
            </Select>
            <FormHelperText error sx={{ marginX: "14px", marginTop: "4px" }}>
              {error && error.message ? t(error.message) : undefined}
            </FormHelperText>
            {value.length > 5 && <Typography variant="body2" sx={{ marginX: "14px", marginTop: "4px", fontWeight: 500, display: "flex", alignItems: "center" }}>
              <Warning color='primary' sx={{ mr: 1 }} />
              {t("beehivesPage.exportForm.warningForLongTime")}
            </Typography>}
          </FormControl>
        )
      }}
      defaultValue={[]}
    />
  );
};

// rendering MUI checkbox field with react-hook-form wrapper
export const CustomCheckboxField = (props: ICustomInputProps) => {
  const { t } = useTranslation();
  const { name, label, control, register, link, linkLabel } = props;
  const id = `${name}-${label}`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        return <FormGroup sx={{ marginY: "0.2em", width: "100%" }}>
          <FormControlLabel
            control={
              <Checkbox
                id={id}
                {...register(name)}
                value={value}
                onChange={onChange}
                size="small"
              />
            }
            label={label ?
              <Typography>
                {t(label)}
                {linkLabel && <Typography component={link ? "a" : "span"} href={link}>
                  {t(linkLabel)}
                </Typography>}
              </Typography> : ""}
          />
          <FormHelperText error sx={{ marginX: "14px", marginTop: "4px" }}>
            {error && error.message ? t(error.message) : undefined}
          </FormHelperText>
        </FormGroup>
      }}
    />
  );
};

// rendering MUI radio button field with react-hook-form wrapper
export const CustomRadioField = (props: ICustomInputProps) => {
  const { t } = useTranslation();
  const { control, name, label, options } = props;
  const id = `${name}-${label}`;
  const labelId = `${name}-${label}-label`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl sx={{ marginY: "0.5em", width: "100%" }}>
          <FormLabel id={labelId}>{label ? t(label) : ""}</FormLabel>
          <RadioGroup
            {...field}
            aria-labelledby={labelId}
            id={id}
          >
            {options &&
              options.map((option) => (
                <FormControlLabel
                  key={option.value + "-" + option.desc}
                  value={option.value}
                  control={<Radio />}
                  label={t(option.desc)}
                />
              ))}
          </RadioGroup>
          <FormHelperText error sx={{ marginX: "14px", marginTop: "4px" }}>
            {error && error.message ? t(error.message) : undefined}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
};

// rendering MUI switch field with react-hook-form wrapper
export const CustomSwitchField = (props: ICustomInputProps) => {
  const { t } = useTranslation();
  const { control, name, label } = props;

  return <Controller
    name={name}
    control={control}
    render={({ field: { onChange, value } }) => (
      <FormControlLabel
        control={<Switch checked={value} onChange={onChange} />}
        label={label ? t(label) : ""}
      />
    )}
  />;
}

// rendering MUI date picker field with react-hook-form wrapper
export const CustomDatePickerField = (props: ICustomInputProps) => {
  const { t } = useTranslation();
  const { control, name, label, defaultValue } = props;
  const locale = I18n.language;

  return <Controller
    name={name}
    defaultValue={defaultValue}
    control={control}
    render={
      ({ field: { onChange, ...restField } }) =>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={
            locale === "it-IT" ? it : locale === "sl-SI" ? sl : enGB
          }
        >
          <DatePicker
            slotProps={{ textField: { size: 'small' } }}
            sx={{ marginY: "0.5em", width: "100%" }}
            label={label ? t(label) : ""}
            onChange={onChange}
            {...restField}
          />
        </LocalizationProvider>
    }
  />;
}